
"use client"

import { useState } from "react"
import ImageComponent from "@/components/ui/ImageComponent"
import { Button } from "@heroui/react"
import { IoTriangle } from "react-icons/io5"

export interface BeforeAfterSliderProps {
  beforeImage: string
  afterImage: string
  before_alt_name: string
  after_alt_name: string
  ariaLabel?: string
}

/** Hide the opposite label when the slider is near that edge (0 = all before, 100 = all after). */
const LABEL_HIDE_EDGE = 12

const BeforeAfterSlider = ({
  beforeImage,
  afterImage,
  before_alt_name,
  after_alt_name,
  ariaLabel = "Adjust before and after image comparison",
}: BeforeAfterSliderProps) => {

  const [sliderValue, setSliderValue] = useState(50)

  const onSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderValue(Number(e.target.value))
  }

  const onDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
  }

  const showBeforeLabel = sliderValue < 100 - LABEL_HIDE_EDGE
  const showAfterLabel = sliderValue > LABEL_HIDE_EDGE

  return (
    <div
      className="relative w-full rounded-3xl overflow-hidden select-none"
      onDragStart={onDragStart}
    >
      {/*
        Invisible aspect-ratio spacer. The two real images below are
        absolutely positioned and need a sized parent — this img provides
        that parent height while staying invisible (`visibility: hidden`).
        We route through Next.js's image optimizer at a tiny width (128px)
        so the spacer is ~5KB instead of the full CMS original (~200-500KB).
        `w-full h-auto` lets the served thumbnail scale to the slot, so the
        parent height tracks the original aspect ratio regardless of the
        served thumbnail size.
      */}
      <img
        src={`/_next/image?url=${encodeURIComponent(afterImage)}&w=128&q=50`}
        alt=""
        className="block w-full h-auto invisible"
        aria-hidden
      />
      {/*
        Both overlay images fill the spacer-sized parent (`w-full h-full`)
        and use `object-cover` so they crop identically — the spacer above
        has already locked the aspect ratio to `afterImage`. Don't use
        `naturalSize` here: that would render the images at their CMS pixel
        dimensions (e.g. 1200×600), which overflows mobile viewports by
        ~3× and breaks the clip-path slider effect.

        `sizes` is a slot-aware override of ImageComponent's default — this
        slider sits inside a card-style swiper that's full-width on mobile
        and roughly half-viewport on desktop.
      */}
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          clipPath: `inset(0 ${100 - sliderValue}% 0 0)`,
        }}
      >
        <ImageComponent
          url={afterImage}
          img_title={after_alt_name}
          object_cover
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
      </div>

      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          clipPath: `inset(0 0 0 ${sliderValue}%)`,
        }}
      >
        <ImageComponent
          url={beforeImage}
          img_title={before_alt_name}
          object_cover
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
      </div>

      { showAfterLabel ? (
        <Button className="absolute top-1/2 -translate-y-1/2 left-2 btn_white_blur -rotate-90 z-20 text-sm! md:text-lg! px-4! min-w-[29px]!">
          Before
        </Button>
      ) : null}

      {showBeforeLabel ? (
        <Button className="absolute top-1/2 -translate-y-1/2 right-2 btn_white_blur -rotate-90 z-20 text-sm! md:text-lg! px-4! min-w-[29px]!">
          After
        </Button>
      ) : null}

      <div
        className="absolute top-0 h-full w-[3px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)] pointer-events-none z-20"
        style={{
          left: `${sliderValue}%`,
          transform: "translateX(-50%)",
        }}
      >
        <div className="relative h-full">
          <IoTriangle className="absolute top-1/2 left-1 text-white rotate-90" />
          <IoTriangle className="absolute top-1/2 right-1 text-white -rotate-90" />
        </div>
      </div>

      <input
        type="range"
        min={0}
        max={100}
        value={sliderValue}
        onChange={onSliderChange}
        aria-label={ariaLabel}
        className="absolute inset-0 w-full h-full opacity-0 cursor-col-resize z-30"
      />
    </div>
  )
}

export default BeforeAfterSlider